interface CompressionOptions {
  maxDimension?: number;
  quality?: number;
  preferredMimeTypes?: string[];
  fallbackMimeType?: string;
}

interface CompressionResult {
  dataUrl: string;
  width: number;
  height: number;
  mimeType: string;
}

const DEFAULT_MAX_DIMENSION = 1600;
const DEFAULT_QUALITY = 0.85;
const DEFAULT_MIME_TYPES = ['image/webp', 'image/jpeg', 'image/png'];

const isBrowserEnvironment =
  typeof window !== 'undefined' &&
  typeof document !== 'undefined' &&
  typeof HTMLCanvasElement !== 'undefined' &&
  typeof Image !== 'undefined';

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Unable to read file as data URL'));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function ensurePositive(value: number | undefined, fallback: number): number {
  return Number.isFinite(value) && typeof value === 'number' && value > 0 ? value : fallback;
}

async function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';
    image.onload = () => resolve(image);
    image.onerror = (event) => {
      reject(
        event instanceof ErrorEvent && event.error instanceof Error
          ? event.error
          : new Error('Failed to load image for compression')
      );
    };
    image.src = dataUrl;
  });
}

function createCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function convertBlobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Unable to convert blob to data URL'));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error('Blob to data URL conversion failed'));
    reader.readAsDataURL(blob);
  });
}

export async function compressFileToDataUrl(
  file: File,
  options: CompressionOptions = {}
): Promise<CompressionResult> {
  const baseDataUrl = await readFileAsDataUrl(file);
  const preferredMimeTypes = Array.from(
    new Set([
      file.type && file.type !== 'application/octet-stream' ? file.type : undefined,
      ...(options.preferredMimeTypes ?? [])
    ].filter(Boolean))
  ) as string[];

  return compressDataUrl(baseDataUrl, {
    ...options,
    preferredMimeTypes: preferredMimeTypes.length ? preferredMimeTypes : options.preferredMimeTypes,
    fallbackMimeType: options.fallbackMimeType ?? file.type ?? 'image/webp'
  });
}

export async function compressDataUrl(
  dataUrl: string,
  options: CompressionOptions = {}
): Promise<CompressionResult> {
  const fallbackMimeType = options.fallbackMimeType ?? 'image/webp';

  if (!isBrowserEnvironment) {
    return {
      dataUrl,
      width: 0,
      height: 0,
      mimeType: fallbackMimeType
    };
  }

  try {
    const image = await loadImage(dataUrl);
    const maxDimension = options.maxDimension ?? DEFAULT_MAX_DIMENSION;
    const scale =
      Math.max(image.width, image.height) > maxDimension
        ? maxDimension / Math.max(image.width, image.height)
        : 1;
    const targetWidth = Math.max(1, Math.round(image.width * scale));
    const targetHeight = Math.max(1, Math.round(image.height * scale));

    const canvas = createCanvas(targetWidth, targetHeight);
    const context = canvas.getContext('2d', { alpha: true });
    if (!context) {
      return {
        dataUrl,
        width: ensurePositive(image.width, 0),
        height: ensurePositive(image.height, 0),
        mimeType: fallbackMimeType
      };
    }
    context.drawImage(image, 0, 0, targetWidth, targetHeight);

    const quality = options.quality ?? DEFAULT_QUALITY;
    const mimeCandidates = options.preferredMimeTypes?.length
      ? options.preferredMimeTypes
      : DEFAULT_MIME_TYPES;

    const blob = await new Promise<Blob | null>((resolve) => {
      const tryEncode = (candidates: string[]): void => {
        if (!candidates.length) {
          resolve(null);
          return;
        }
        const [current, ...rest] = candidates;
        canvas.toBlob(
          (result) => {
            if (result) {
              resolve(result);
            } else {
              tryEncode(rest);
            }
          },
          current,
          quality
        );
      };
      tryEncode(mimeCandidates);
    });

    if (!blob) {
      const autoDataUrl = canvas.toDataURL(fallbackMimeType, quality);
      return {
        dataUrl: autoDataUrl,
        width: targetWidth,
        height: targetHeight,
        mimeType: fallbackMimeType
      };
    }

    const compressedDataUrl = await convertBlobToDataUrl(blob);

    return {
      dataUrl: compressedDataUrl,
      width: targetWidth,
      height: targetHeight,
      mimeType: blob.type || fallbackMimeType
    };
  } catch (error) {
    console.warn('Image compression failed. Falling back to original data URL.', error);
    return {
      dataUrl,
      width: 0,
      height: 0,
      mimeType: fallbackMimeType
    };
  }
}
